import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import {
  Package, Plus, Check, X, Loader2, ExternalLink, DollarSign, ClipboardList,
  ChevronRight, Truck, PackageCheck, ShoppingCart, LayoutGrid, List as ListIcon,
  Camera, MessageSquare, Send, Trash2, FileText, Sparkles, Search, Wallet,
} from "lucide-react";
import { PayReceiptModal } from "../components/PayReceiptModal";
import { MarkPurchasedModal } from "../components/MarkPurchasedModal";
import { SupplierAutocomplete } from "../components/SupplierAutocomplete";

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";
const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const STATUS_UI = {
  pending_pm:          { label: "Awaiting PM",       color: "bg-amber-50 text-amber-700 border-amber-200" },
  pending_purchasing:  { label: "With Purchasing",   color: "bg-blue-50 text-blue-700 border-blue-200" },
  pending_approval:    { label: "Awaiting Quote Approval", color: "bg-purple-50 text-purple-700 border-purple-200" },
  pending_director:    { label: "Escalated Director",color: "bg-orange-50 text-orange-700 border-orange-200" },
  approved_purchasing: { label: "Purchasing works",  color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  purchased:           { label: "Received / Closed", color: "bg-slate-100 text-slate-700 border-slate-200" },
  rejected:            { label: "Rejected",          color: "bg-red-50 text-red-700 border-red-200" },
};

const ITEM_STATUS_UI = {
  pending:   { label: "Pending",   color: "bg-slate-100 text-slate-600 border-slate-200",   icon: Package },
  quoted:    { label: "Quoted",    color: "bg-blue-50 text-blue-700 border-blue-200",       icon: DollarSign },
  ordered:   { label: "Ordered",   color: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: Truck },
  received:  { label: "Received",  color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: PackageCheck },
  cancelled: { label: "Cancelled", color: "bg-red-50 text-red-600 border-red-200",           icon: X },
};

const KANBAN_COLS = [
  { key: "to_quote",  label: "To quote",        includes: ["pending_pm", "pending_purchasing"] },
  { key: "approving", label: "Awaiting approval", includes: ["pending_approval"] },
  { key: "quoted",    label: "Approved / Working",includes: ["pending_director", "approved_purchasing"] },
  { key: "received",  label: "Received",        includes: ["purchased"] },
];

export default function MaterialRequests() {
  const { user } = useAuth();
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [view, setView] = useState("list");   // list | kanban
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const status = filter === "open"
        ? "pending_pm,pending_purchasing,pending_approval,pending_director,approved_purchasing"
        : filter === "purchased" ? "purchased"
        : filter === "rejected" ? "rejected" : "";
      const r = await api.get(`/material-requests${status ? `?status=${status}` : ""}`);
      setRows(r);
    } catch { toast.error(t("error")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const refresh = async () => { await load(); };

  return (
    <div className="space-y-6" data-testid="material-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Package className="w-7 h-7" /> {t("materialRequests") || "Material Requests"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t("mrSubtitle") || "Field material requests awaiting approval / purchase."}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          <button
            data-testid="mr-view-list"
            onClick={() => setView("list")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 ${
              view === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <ListIcon className="w-3.5 h-3.5" /> {t("list") || "List"}
          </button>
          <button
            data-testid="mr-view-kanban"
            onClick={() => setView("kanban")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 ${
              view === "kanban" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Kanban
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          ["open", t("mrOpen") || "Open"],
          ["purchased", t("mrPurchased") || "Purchased"],
          ["rejected", t("mrRejected") || "Rejected"],
          ["all", t("mrAll") || "All"],
        ].map(([k, lbl]) => (
          <button
            key={k}
            data-testid={`mr-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {loading && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}

      {!loading && rows.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl py-16 text-center text-slate-400 text-sm">
          {t("noMaterialRequests") || "No material requests yet."}
        </div>
      )}

      {!loading && rows.length > 0 && view === "list" && (
        <MRListView rows={rows} onOpen={setDetail} t={t} />
      )}

      {!loading && rows.length > 0 && view === "kanban" && (
        <MRKanbanView rows={rows} onOpen={setDetail} t={t} />
      )}

      {detail && (
        <MRDetail
          req={detail}
          user={user}
          t={t}
          onClose={() => setDetail(null)}
          onChange={async () => {
            await refresh();
            // Reload the detail from the fresh list to keep the modal open with new data
            const fresh = await api.get(`/material-requests/${detail.id}`).catch(() => null);
            setDetail(fresh || null);
          }}
        />
      )}
    </div>
  );
}

/* ============================== List view ============================== */

function MRListView({ rows, onOpen, t }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map((r) => {
        const ui = STATUS_UI[r.status] || { label: r.status, color: "bg-slate-100 text-slate-700 border-slate-200" };
        const receivedCount = (r.items || []).filter((it) => it.status === "received").length;
        return (
          <button
            key={r.id}
            data-testid={`mr-card-${r.id}`}
            onClick={() => onOpen(r)}
            className="bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-slate-400 hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-xs text-slate-400 font-medium">{r.number}</div>
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {r.ticket_number} — {r.ticket_title}
                </div>
                <div className="text-[11px] text-slate-500 truncate mt-0.5">
                  {r.property_name || ""}{r.unit_name ? ` • ${r.unit_name}` : ""}
                </div>
              </div>
              <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full border ${ui.color} whitespace-nowrap`}>
                {ui.label}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
              <div>
                {r.items.length} {t("items") || "items"} • {r.requested_by_name}
                {receivedCount > 0 && (
                  <span className="ml-2 text-emerald-600 font-semibold">
                    ✓ {receivedCount}/{r.items.length} received
                  </span>
                )}
              </div>
              {((r.actual_total ?? r.estimated_total) || 0) > 0 && (
                <div className="font-semibold text-slate-800 tabular-nums">
                  {money(r.actual_total ?? r.estimated_total)}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ============================== Kanban view ============================== */

function MRKanbanView({ rows, onOpen, t }) {
  // Bucketize each request into a kanban column based on the aggregate state of its items.
  const columns = useMemo(() => {
    const c = { to_quote: [], quoted: [], ordered: [], received: [] };
    for (const r of rows) {
      if (r.status === "rejected") continue;
      const items = r.items || [];
      const allReceived = items.length > 0 && items.every((it) => it.status === "received");
      const anyOrdered = items.some((it) => it.status === "ordered");
      const anyQuoted = items.some((it) => it.status === "quoted" || (parseFloat(it.estimated_price) || 0) > 0);
      if (allReceived || r.status === "purchased") c.received.push(r);
      else if (anyOrdered) c.ordered.push(r);
      else if (anyQuoted || r.status === "approved_purchasing" || r.status === "pending_director") c.quoted.push(r);
      else c.to_quote.push(r);
    }
    return c;
  }, [rows]);

  const colConf = [
    { key: "to_quote", label: t("kToQuote") || "To Quote",  accent: "bg-amber-50 border-amber-200" },
    { key: "quoted",   label: t("kQuoted") || "Quoted",     accent: "bg-blue-50 border-blue-200" },
    { key: "ordered",  label: t("kOrdered") || "Ordered",   accent: "bg-indigo-50 border-indigo-200" },
    { key: "received", label: t("kReceived") || "Received", accent: "bg-emerald-50 border-emerald-200" },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="mr-kanban">
      {colConf.map((c) => (
        <div key={c.key} className={`rounded-xl border ${c.accent} p-3 min-h-[400px]`} data-testid={`mr-kanban-col-${c.key}`}>
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-700">{c.label}</div>
            <div className="text-[10px] font-bold text-slate-500 bg-white/70 rounded-full px-2 py-0.5">
              {columns[c.key].length}
            </div>
          </div>
          <div className="space-y-2">
            {columns[c.key].length === 0 && (
              <div className="text-center text-[11px] text-slate-400 py-6">—</div>
            )}
            {columns[c.key].map((r) => (
              <button
                key={r.id}
                data-testid={`mr-kanban-card-${r.id}`}
                onClick={() => onOpen(r)}
                className="w-full bg-white border border-slate-200 rounded-lg p-3 text-left hover:shadow-md hover:border-slate-400 transition"
              >
                <div className="text-[10px] text-slate-400 font-medium">{r.number}</div>
                <div className="text-xs font-semibold text-slate-900 truncate">
                  {r.ticket_number} — {r.ticket_title}
                </div>
                <div className="text-[10px] text-slate-500 truncate mt-0.5">{r.property_name || ""}</div>
                <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500">
                  <span>{r.items.length} items</span>
                  {((r.actual_total ?? r.estimated_total) || 0) > 0 && (
                    <span className="font-semibold text-slate-700 tabular-nums">
                      {money(r.actual_total ?? r.estimated_total)}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================== Detail modal ============================== */

function MRDetail({ req, user, t, onClose, onChange }) {  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [receiveTarget, setReceiveTarget] = useState(null);   // item to mark as received
  const [commentText, setCommentText] = useState("");
  const [invoiceScanOpen, setInvoiceScanOpen] = useState(false);

  useEffect(() => {
    if (["purchasing", "admin", "manager"].includes(user.role)) {
      api.get("/suppliers").then(setSuppliers).catch(() => {});
    }
  }, [user.role]);

  const canPMApprove = ["pm", "admin", "manager"].includes(user.role) && req.status === "pending_pm";
  const canPurchaseEdit = ["purchasing", "admin", "manager"].includes(user.role)
    && ["pending_purchasing", "approved_purchasing", "pending_director"].includes(req.status);
  const canAddQuotes = ["purchasing", "admin", "manager"].includes(user.role)
    && ["pending_purchasing", "pending_approval"].includes(req.status);
  const canSubmitForApproval = ["purchasing", "admin", "manager"].includes(user.role)
    && req.status === "pending_purchasing" && (req.quotes || []).length > 0;
  const canApproveQuote = ["pm", "analista", "admin", "manager"].includes(user.role)
    && req.status === "pending_approval";
  const canDirector = ["director", "admin"].includes(user.role) && req.status === "pending_director";
  const canReject = ["pm", "director", "admin", "manager"].includes(user.role)
    && !["purchased", "rejected"].includes(req.status);
  const canReceive = ["technician", "pm", "purchasing", "admin", "manager"].includes(user.role);
  const canPay = ["purchasing", "admin", "manager", "financeiro"].includes(user.role)
    && req.status === "purchased" && !req.paid;
  const canMarkPurchased = ["purchasing", "admin", "manager"].includes(user.role)
    && ["approved_purchasing", "pending_purchasing"].includes(req.status);
  const [payOpen, setPayOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const canComment = true;
  const canGeneratePO = ["purchasing", "admin", "manager"].includes(user.role)
    && (req.items || []).some((it) => (it.supplier_id || it.supplier_name) && !it.po_id
                                       && it.status !== "received" && it.status !== "cancelled");

  const ui = STATUS_UI[req.status] || { label: req.status, color: "bg-slate-100 text-slate-700 border-slate-200" };

  const callPost = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/material-requests/${req.id}${path}`, body || {});
      toast.success(t("saved"));
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  const patchItem = async (item_id, payload) => {
    setBusy(true);
    try {
      await api.patch(`/material-requests/${req.id}/items/${item_id}`, payload);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  const generatePO = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/material-requests/${req.id}/generate-po`, {});
      toast.success(`${r.purchase_orders.length} PO(s) created`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  const sendComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(`/material-requests/${req.id}/comments`, { text });
      setCommentText("");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs text-slate-400 font-medium">{req.number}</div>
            <h2 className="text-lg font-semibold text-slate-900">{req.ticket_number} — {req.ticket_title}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full border ${ui.color}`}>{ui.label}</span>
            {req.urgency === "urgent" && (
              <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-red-100 text-red-700 border border-red-200">Urgent</span>
            )}
            <span className="text-xs text-slate-500">
              {t("requestedBy") || "Requested by"} <span className="font-semibold">{req.requested_by_name}</span>
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><div className="text-slate-400 font-bold uppercase tracking-wide mb-0.5">{t("property") || "Property"}</div><div>{req.property_name || "—"}</div></div>
            <div><div className="text-slate-400 font-bold uppercase tracking-wide mb-0.5">{t("unit") || "Unit"}</div><div>{req.unit_name || "—"}</div></div>
          </div>

          {req.notes && (
            <div>
              <div className="text-slate-400 font-bold uppercase tracking-wide text-xs mb-1">{t("notes")}</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{req.notes}</p>
            </div>
          )}

          {(req.photos || []).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {(req.photos || []).map((p, i) => (
                <a key={i} href={p} target="_blank" rel="noreferrer" className="w-14 h-14 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden hover:opacity-80">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}

          {/* Items table */}
          <div>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="text-slate-400 font-bold uppercase tracking-wide text-xs">{t("items") || "Items"}</div>
              <div className="flex items-center gap-2 flex-wrap">
                {canPurchaseEdit && (
                  <button
                    data-testid="mr-scan-invoice"
                    disabled={busy}
                    onClick={() => setInvoiceScanOpen(true)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-fuchsia-600 rounded-lg px-3 py-1.5 hover:bg-fuchsia-700 disabled:opacity-40"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> {t("scanInvoice") || "Scan invoice (AI)"}
                  </button>
                )}
                {canMarkPurchased && (
                  <button
                    data-testid="mr-mark-purchased"
                    disabled={busy}
                    onClick={() => setPurchaseOpen(true)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 rounded-lg px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-40"
                  >
                    <Wallet className="w-3.5 h-3.5" /> {t("markPurchased") || "Mark as Purchased"}
                  </button>
                )}
                {canGeneratePO && (
                  <button
                    data-testid="mr-generate-po"
                    disabled={busy}
                    onClick={generatePO}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg px-3 py-1.5 hover:bg-indigo-700 disabled:opacity-40"
                  >
                    <FileText className="w-3.5 h-3.5" /> {t("generatePO") || "Generate PO"}
                  </button>
                )}
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-2 bg-slate-50 px-3 py-2 text-[10px] uppercase font-bold tracking-wider text-slate-500">
                <div className="col-span-4">Description</div>
                <div className="col-span-2 text-right">Qty</div>
                <div className="col-span-3">Supplier</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-1 text-right"></div>
              </div>
              <div className="divide-y divide-slate-100">
                {(req.items || []).map((it) => (
                  <MRItemRow
                    key={it.id}
                    item={it}
                    canEdit={canPurchaseEdit}
                    canReceive={canReceive && it.status !== "received" && it.status !== "cancelled"}
                    suppliers={suppliers}
                    busy={busy}
                    onPatch={(payload) => patchItem(it.id, payload)}
                    onReceive={() => setReceiveTarget(it)}
                  />
                ))}
              </div>
            </div>
            <div className="mt-2 flex items-center justify-end gap-3 text-xs">
              <span className="font-bold uppercase text-slate-400">
                {req.actual_total != null ? (t("actualTotal") || "Actual total") : (t("estimatedTotal") || "Estimated total")}
              </span>
              <span className="text-lg font-bold text-slate-900 tabular-nums" data-testid="mr-total">
                {money(req.actual_total ?? req.estimated_total)}
              </span>
            </div>
          </div>

          {/* Quotes / RFQ section — purchasing collects up to 3 quotes and submits for approval */}
          {(canAddQuotes || (req.quotes || []).length > 0 || canApproveQuote) && (
            <QuotesSection req={req} user={user} onChange={onChange}
              canAddQuotes={canAddQuotes}
              canSubmitForApproval={canSubmitForApproval}
              canApproveQuote={canApproveQuote} />
          )}

          {/* Approval actions */}
          {(canPMApprove || canDirector || canReject) && (
            <div className="space-y-3 border-t border-slate-200 pt-4">
              <textarea
                data-testid="mr-action-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder={t("addNotesOptional") || "Add notes (optional)"}
                className={inputCls}
              />
              <div className="flex flex-wrap gap-2">
                {canPMApprove && (
                  <button
                    data-testid="mr-pm-approve"
                    disabled={busy}
                    onClick={() => callPost("/pm-approve", { notes })}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-medium px-4 py-2 hover:bg-emerald-700 disabled:opacity-40"
                  >
                    <Check className="w-4 h-4" /> {t("pmApprove") || "PM Approve"}
                  </button>
                )}
                {canDirector && (
                  <button
                    data-testid="mr-director-approve"
                    disabled={busy}
                    onClick={() => callPost("/director-approve", { notes })}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-medium px-4 py-2 hover:bg-emerald-700 disabled:opacity-40"
                  >
                    <Check className="w-4 h-4" /> {t("directorApprove") || "Director Approve"}
                  </button>
                )}
                {canReject && (
                  <button
                    data-testid="mr-reject"
                    disabled={busy}
                    onClick={() => {
                      const reason = window.prompt(t("rejectReasonPrompt") || "Reason for rejection:", "");
                      if (!reason) return;
                      callPost("/reject", { reason });
                    }}
                    className="inline-flex items-center gap-2 rounded-lg bg-red-500 text-white text-sm font-medium px-4 py-2 hover:bg-red-600 disabled:opacity-40"
                  >
                    <X className="w-4 h-4" /> {t("reject") || "Reject"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Payment action — appears once the receipt is fully received & unpaid */}
          {(canPay || req.paid) && (
            <div className="border-t border-slate-200 pt-4">
              <div className="text-slate-400 font-bold uppercase tracking-wide text-xs mb-2 flex items-center gap-1.5">
                <Wallet className="w-3.5 h-3.5" /> {t("payment") || "Payment"}
              </div>
              {req.paid ? (
                <div className="inline-flex items-center gap-2 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
                     data-testid={`mr-paid-badge-${req.id}`}>
                  <Wallet className="w-4 h-4" /> Paid · {money(req.paid_amount || 0)}
                  {req.payment_method && <span className="text-emerald-600">· {req.payment_method}</span>}
                  {req.paid_at && <span className="text-emerald-600 tabular-nums">· {req.paid_at.slice(0, 10)}</span>}
                </div>
              ) : (
                <button
                  data-testid={`mr-pay-${req.id}`}
                  onClick={() => setPayOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800"
                >
                  <Wallet className="w-4 h-4" /> {t("markPaid") || "Mark paid & post to ledger"}
                </button>
              )}
            </div>
          )}

          {/* Comments/chat */}
          <div className="border-t border-slate-200 pt-4">
            <div className="text-slate-400 font-bold uppercase tracking-wide text-xs mb-2 flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> {t("comments") || "Comments"}
              {(req.comments || []).length > 0 && (
                <span className="text-[10px] font-normal text-slate-400">({req.comments.length})</span>
              )}
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
              {(req.comments || []).length === 0 && (
                <div className="text-xs text-slate-400 italic">{t("noComments") || "No messages yet."}</div>
              )}
              {(req.comments || []).map((c) => (
                <div
                  key={c.id}
                  data-testid={`mr-comment-${c.id}`}
                  className={`rounded-lg p-2.5 text-sm ${
                    c.user_id === user.id
                      ? "bg-blue-50 border border-blue-100 ml-8"
                      : "bg-slate-50 border border-slate-100 mr-8"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-semibold text-slate-700">
                      {c.user_name} <span className="text-slate-400 font-normal">({c.user_role})</span>
                    </span>
                    <span className="text-[10px] text-slate-400">{c.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-slate-700 whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
            </div>
            {canComment && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  data-testid="mr-comment-input"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) sendComment(); }}
                  placeholder={t("commentPlaceholder") || "Ask a question, leave a note…"}
                  className={inputCls}
                />
                <button
                  data-testid="mr-comment-send"
                  disabled={busy || !commentText.trim()}
                  onClick={sendComment}
                  className="p-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Activity log */}
          {(req.activity || []).length > 0 && (
            <div className="border-t border-slate-200 pt-4">
              <div className="text-slate-400 font-bold uppercase tracking-wide text-xs mb-2 flex items-center gap-1.5">
                <ClipboardList className="w-3.5 h-3.5" /> {t("activity") || "Activity"}
              </div>
              <ul className="space-y-1 text-xs text-slate-500 max-h-48 overflow-y-auto pr-1">
                {req.activity.map((a, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-slate-300" />
                    <span>
                      <span className="font-semibold text-slate-700">{a.user_name}</span> — {a.action}
                      {a.detail ? `: ${a.detail}` : ""}{" "}
                      <span className="text-slate-400">• {a.at?.slice(0, 16).replace("T", " ")}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {receiveTarget && (
        <ReceiveModal
          item={receiveTarget}
          t={t}
          busy={busy}
          onClose={() => setReceiveTarget(null)}
          onSubmit={async (payload) => {
            setBusy(true);
            try {
              await api.post(`/material-requests/${req.id}/items/${receiveTarget.id}/receive`, payload);
              toast.success(t("saved"));
              setReceiveTarget(null);
              onChange();
            } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
            finally { setBusy(false); }
          }}
        />
      )}

      {invoiceScanOpen && (
        <InvoiceScanModal
          req={req}
          t={t}
          suppliers={suppliers}
          onClose={() => setInvoiceScanOpen(false)}
          onDone={async () => {
            setInvoiceScanOpen(false);
            await onChange();
          }}
        />
      )}

      {payOpen && (
        <PayReceiptModal
          entity="material_request"
          id={req.id}
          number={req.number || `MR-${req.id?.slice(0, 6)}`}
          defaultAmount={req.total || 0}
          onClose={() => setPayOpen(false)}
          onDone={async () => { setPayOpen(false); await onChange(); }}
        />
      )}

      {purchaseOpen && (
        <MarkPurchasedModal
          req={req}
          onClose={() => setPurchaseOpen(false)}
          onDone={async () => { setPurchaseOpen(false); await onChange(); }}
        />
      )}
    </div>
  );
}

/* ============================== Per-item row ============================== */

function MRItemRow({ item, canEdit, canReceive, suppliers, busy, onPatch, onReceive }) {
  const [supplierId, setSupplierId] = useState(item.supplier_id || "");
  const [price, setPrice] = useState(item.estimated_price ?? 0);

  useEffect(() => { setSupplierId(item.supplier_id || ""); setPrice(item.estimated_price ?? 0); }, [item.id, item.estimated_price, item.supplier_id]);

  const status = item.status || "pending";
  const stUi = ITEM_STATUS_UI[status] || ITEM_STATUS_UI.pending;
  const StIcon = stUi.icon;
  const totalItem = (parseFloat(item.quantity) || 0) * (parseFloat(price) || 0);

  const commitSupplier = (v) => {
    setSupplierId(v);
    const sup = suppliers.find((s) => s.id === v);
    onPatch({ supplier_id: v || null, supplier_name: sup?.name || "" });
  };
  const commitPrice = () => {
    const p = parseFloat(price) || 0;
    if ((parseFloat(item.estimated_price) || 0) === p) return;
    onPatch({ estimated_price: p });
  };

  return (
    <div className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center hover:bg-slate-50/40" data-testid={`mr-item-row-${item.id}`}>
      <div className="col-span-12 md:col-span-4 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm text-slate-800 truncate flex-1">{item.description}</div>
          {canEdit && (
            <CatalogPicker
              currentDescription={item.description}
              onPick={(mat) => onPatch({
                description: mat.name,
                estimated_price: mat.last_price || 0,
                supplier_name: mat.last_supplier || undefined,
              })}
            />
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
          <span
            className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full border ${stUi.color}`}
            data-testid={`mr-item-status-${item.id}`}
          >
            <StIcon className="w-3 h-3" /> {stUi.label}
          </span>
          {item.link && (
            <a href={item.link} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-0.5">
              <ExternalLink className="w-3 h-3" /> link
            </a>
          )}
          {item.po_number && (
            <span className="text-[11px] text-indigo-600 font-medium">PO {item.po_number}</span>
          )}
        </div>
      </div>

      <div className="col-span-3 md:col-span-2 text-right text-sm text-slate-700 tabular-nums">
        {item.quantity} {item.unit || ""}
      </div>

      <div className="col-span-9 md:col-span-3">
        {canEdit ? (
          <select
            data-testid={`mr-item-supplier-${item.id}`}
            value={supplierId}
            onChange={(e) => commitSupplier(e.target.value)}
            disabled={busy}
            className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
          >
            <option value="">— supplier —</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        ) : (
          <span className="text-xs text-slate-600">{item.supplier_name || <span className="text-slate-300">—</span>}</span>
        )}
      </div>

      <div className="col-span-8 md:col-span-2 text-right">
        {canEdit ? (
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
            <input
              data-testid={`mr-item-price-${item.id}`}
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              onBlur={commitPrice}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              disabled={busy}
              className="w-full rounded-lg border border-slate-300 pl-5 pr-2 py-1.5 text-xs text-right tabular-nums focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
            />
          </div>
        ) : (
          <div className="text-xs tabular-nums">
            <div className="text-slate-700 font-semibold">{money(totalItem)}</div>
            <div className="text-slate-400">{money(item.estimated_price || 0)} ea</div>
          </div>
        )}
      </div>

      <div className="col-span-4 md:col-span-1 flex justify-end gap-1">
        {canReceive && (
          <button
            data-testid={`mr-item-receive-${item.id}`}
            disabled={busy}
            onClick={onReceive}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
            title="Mark received"
          >
            <PackageCheck className="w-4 h-4" />
          </button>
        )}
        {canEdit && status !== "received" && (
          <button
            data-testid={`mr-item-cancel-${item.id}`}
            disabled={busy}
            onClick={() => { if (window.confirm("Cancel this item?")) onPatch({ status: "cancelled" }); }}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
            title="Cancel item"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        {status === "received" && item.receipt_photo && (
          <a
            href={item.receipt_photo}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-emerald-600 hover:bg-emerald-50"
            title="Receipt photo"
          >
            <Camera className="w-4 h-4" />
          </a>
        )}
      </div>
    </div>
  );
}

/* ============================== Receive modal ============================== */

function ReceiveModal({ item, t, busy, onClose, onSubmit }) {
  const [photo, setPhoto] = useState(null);
  const [notes, setNotes] = useState("");
  const [receivedQty, setReceivedQty] = useState(item.quantity || 1);

  const onFile = (f) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(f);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-2 md:p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="mr-receive-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="text-xs text-slate-400">{t("receiveItem") || "Receive item"}</div>
            <div className="text-sm font-semibold text-slate-900 truncate">{item.description}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
              {t("receivedQuantity") || "Received quantity"}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={receivedQty}
              onChange={(e) => setReceivedQty(e.target.value)}
              className={inputCls}
              data-testid="mr-receive-qty"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
              {t("receiptPhoto") || "Receipt / delivery photo"}
            </label>
            {photo ? (
              <div className="relative">
                <img src={photo} alt="" className="w-full h-40 object-cover rounded-lg border border-slate-200" />
                <button
                  onClick={() => setPhoto(null)}
                  className="absolute top-2 right-2 bg-white/90 border border-slate-200 rounded-md px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-white"
                >
                  {t("remove") || "Remove"}
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-slate-300 rounded-lg text-slate-400 hover:border-slate-500 cursor-pointer">
                <Camera className="w-8 h-8 mb-2" />
                <span className="text-xs">{t("tapToAddPhoto") || "Tap to add photo"}</span>
                <input
                  data-testid="mr-receive-photo"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
              </label>
            )}
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
              {t("notes")}
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("receiveNotesPlaceholder") || "e.g. Delivered at 2pm, box slightly damaged"}
              className={inputCls}
              data-testid="mr-receive-notes"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 py-3 border-t border-slate-200">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-lg px-3 py-2 text-sm border border-slate-300 hover:bg-slate-50 disabled:opacity-40"
          >
            {t("cancel") || "Cancel"}
          </button>
          <button
            data-testid="mr-receive-confirm"
            onClick={() => onSubmit({
              receipt_photo: photo || undefined,
              receipt_notes: notes,
              received_quantity: parseFloat(receivedQty) || item.quantity,
            })}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-medium px-4 py-2 hover:bg-emerald-700 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />}
            {t("markReceived") || "Mark received"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================== Catalog picker (typeahead) ============================== */

function CatalogPicker({ currentDescription, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(currentDescription || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 320 });
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newPrice, setNewPrice] = useState("");
  const [newSupplier, setNewSupplier] = useState("");
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/materials/catalog${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`);
        setRows(r);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openPanel = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 6, left: Math.max(8, rect.right - 340), width: 340 });
    setQ(currentDescription || "");
    setCreating(false);
    setOpen(true);
  };

  const saveNewMaterial = async () => {
    const name = q.trim();
    const price = parseFloat(newPrice) || 0;
    if (!name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const created = await api.post("/materials", {
        material_name: name,
        quantity: 1,
        unit_price: price,
        supplier: newSupplier.trim(),
        trade: "general_repair",
      });
      // Immediately apply this new material to the item
      onPick({
        name: created.material_name,
        last_price: created.unit_price,
        last_supplier: created.supplier,
      });
      toast.success("Added to catalog");
      setOpen(false);
      setCreating(false);
      setNewPrice(""); setNewSupplier("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button
        ref={btnRef}
        data-testid="mr-catalog-btn"
        onClick={openPanel}
        className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-md px-1.5 py-0.5"
        title="Find in catalog"
      >
        <Search className="w-3 h-3" /> catalog
      </button>
      {open && (
        <div
          ref={ref}
          data-testid="mr-catalog-panel"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          className="fixed z-[70] bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden"
        >
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              data-testid="mr-catalog-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search material catalog…"
              className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
            />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading && <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>}
            {!loading && rows.length === 0 && !creating && (
              <div className="text-center text-xs text-slate-400 py-4 px-3">
                No matches — try another word or
                <button
                  data-testid="mr-catalog-add-new"
                  onClick={() => { setCreating(true); setNewPrice(""); setNewSupplier(""); }}
                  className="ml-1 inline-flex items-center gap-0.5 text-blue-600 hover:underline font-semibold"
                >
                  <Plus className="w-3 h-3" /> add to catalog
                </button>
              </div>
            )}
            {!loading && creating && (
              <div className="p-3 space-y-2 border-t border-slate-100" data-testid="mr-catalog-new-form">
                <div className="text-[10px] font-bold uppercase text-slate-500">New material</div>
                <div className="text-xs text-slate-700 line-clamp-2">{q}</div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                    <input
                      data-testid="mr-catalog-new-price"
                      type="number" step="0.01" min="0" placeholder="Unit price"
                      value={newPrice} onChange={(e) => setNewPrice(e.target.value)}
                      className="w-full rounded-md border border-slate-200 pl-5 pr-2 py-1.5 text-xs text-right tabular-nums focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
                    />
                  </div>
                  <input
                    data-testid="mr-catalog-new-supplier"
                    placeholder="Supplier"
                    value={newSupplier} onChange={(e) => setNewSupplier(e.target.value)}
                    className="flex-1 rounded-md border border-slate-200 px-2 py-1.5 text-xs focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={() => setCreating(false)}
                    disabled={saving}
                    className="text-xs px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50"
                  >Cancel</button>
                  <button
                    data-testid="mr-catalog-new-save"
                    onClick={saveNewMaterial}
                    disabled={saving}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-blue-600 rounded-md px-2 py-1 hover:bg-blue-700 disabled:opacity-40"
                  >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Save & apply
                  </button>
                </div>
              </div>
            )}
            {!loading && rows.map((m) => (
              <button
                key={m.name_key}
                data-testid={`mr-catalog-row-${m.name_key.replace(/[^a-z0-9]/g, "_").slice(0, 40)}`}
                onClick={() => { onPick(m); setOpen(false); }}
                className="w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
              >
                <div className="text-xs font-medium text-slate-800 line-clamp-2">{m.name}</div>
                <div className="flex items-center justify-between mt-0.5 text-[10px] text-slate-500">
                  <span className="truncate">{m.last_supplier}{m.trade ? ` · ${m.trade}` : ""}</span>
                  <span className="font-bold text-slate-800 tabular-nums shrink-0 ml-2">
                    ${(m.last_price || 0).toFixed(2)}
                  </span>
                </div>
              </button>
            ))}
            {!loading && rows.length > 0 && !creating && (
              <button
                data-testid="mr-catalog-add-new-bottom"
                onClick={() => { setCreating(true); setNewPrice(""); setNewSupplier(""); }}
                className="w-full text-left px-3 py-2 border-t border-slate-200 bg-slate-50 text-xs font-semibold text-blue-600 hover:bg-slate-100 inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add &ldquo;{q.slice(0, 30)}{q.length > 30 ? "…" : ""}&rdquo; as new material
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ============================== Invoice scan modal (AI OCR) ============================== */

function InvoiceScanModal({ req, t, suppliers, onClose, onDone }) {
  const [photo, setPhoto] = useState(null);
  const [supplierId, setSupplierId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = (f) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(f);
  };

  const run = async () => {
    if (!photo) { toast.error(t("addPhoto") || "Add a photo"); return; }
    setBusy(true);
    try {
      const sup = suppliers.find((s) => s.id === supplierId);
      const r = await api.post(`/material-requests/${req.id}/scan-invoice`, {
        photos: [photo],
        supplier_id: supplierId || null,
        supplier_name: sup?.name || "",
      });
      setResult(r);
      toast.success(`${r.matched.length} matched / ${r.unmatched_lines.length} unmatched`);
    } catch (e) { toast.error(e?.response?.data?.detail || "OCR failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 md:p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()} data-testid="mr-scan-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-fuchsia-600" />
              <h3 className="text-sm font-semibold text-slate-900">
                {t("scanInvoice") || "Scan invoice (AI)"}
              </h3>
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {t("scanInvoiceHint") ||
                "Upload the supplier receipt/invoice. AI reads the lines and auto-fills prices per item."}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!result && (
            <>
              <div>
                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
                  {t("supplier") || "Supplier"} <span className="text-slate-400 font-normal">(optional — AI will detect it)</span>
                </label>
                <select
                  data-testid="mr-scan-supplier"
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                  className={inputCls}
                >
                  <option value="">— auto-detect —</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
                  {t("invoicePhoto") || "Invoice photo"}
                </label>
                {photo ? (
                  <div className="relative">
                    <img src={photo} alt="" className="w-full max-h-56 object-contain bg-slate-50 rounded-lg border border-slate-200" />
                    <button
                      onClick={() => setPhoto(null)}
                      className="absolute top-2 right-2 bg-white/95 border border-slate-200 rounded-md px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-white"
                    >
                      {t("remove") || "Remove"}
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-slate-300 rounded-lg text-slate-400 hover:border-fuchsia-500 cursor-pointer">
                    <Camera className="w-8 h-8 mb-2" />
                    <span className="text-xs">{t("tapToAddPhoto") || "Tap to add photo"}</span>
                    <input
                      data-testid="mr-scan-photo"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => upload(e.target.files?.[0])}
                    />
                  </label>
                )}
              </div>
            </>
          )}

          {result && (
            <div className="space-y-3">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                <div className="text-sm font-semibold text-emerald-800">
                  ✓ Scanned — {result.ocr?.supplier || "Unknown supplier"}
                </div>
                <div className="text-xs text-emerald-700 mt-0.5">
                  Invoice {result.ocr?.invoice_number || result.ocr?.transaction_id || "—"} · {result.ocr?.items?.length || 0} lines · Total ${(result.ocr?.total || 0).toFixed(2)}
                </div>
                {result.catalog_added > 0 && (
                  <div className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> {result.catalog_added} line(s) added to catalog for future scans
                  </div>
                )}
              </div>

              {result.matched?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">
                    Auto-filled ({result.matched.length})
                  </div>
                  <ul className="space-y-1 text-xs">
                    {result.matched.map((m) => (
                      <li key={m.item_id} className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate font-medium text-slate-800">{m.description}</span>
                          <span className="font-bold text-slate-900 tabular-nums shrink-0">${m.unit_price.toFixed(2)}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">→ from invoice: {m.invoice_line}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.unmatched_lines?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-amber-700 mb-1">
                    Unmatched invoice lines ({result.unmatched_lines.length})
                  </div>
                  <ul className="space-y-1 text-xs">
                    {result.unmatched_lines.map((u, i) => (
                      <li key={i} className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5 flex items-center justify-between gap-2">
                        <span className="truncate text-amber-900">{u.description}</span>
                        <span className="font-semibold text-amber-900 tabular-nums shrink-0">${(u.unit_price || 0).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-lg px-3 py-2 text-sm border border-slate-300 hover:bg-slate-50 disabled:opacity-40"
          >
            {result ? (t("close") || "Close") : (t("cancel") || "Cancel")}
          </button>
          {!result ? (
            <button
              data-testid="mr-scan-run"
              onClick={run}
              disabled={busy || !photo}
              className="inline-flex items-center gap-2 rounded-lg bg-fuchsia-600 text-white text-sm font-medium px-4 py-2 hover:bg-fuchsia-700 disabled:opacity-40"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {t("runScan") || "Run scan"}
            </button>
          ) : (
            <button
              data-testid="mr-scan-done"
              onClick={onDone}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800"
            >
              <Check className="w-4 h-4" /> {t("done") || "Done"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}



/* ================= QuotesSection — RFQ flow (up to 3 quotes per MR) ================= */
function QuotesSection({ req, user, onChange, canAddQuotes, canSubmitForApproval, canApproveQuote }) {
  const [form, setForm] = useState({ supplier_name: "", total: 0, delivery_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState("");
  const quotes = req.quotes || [];
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => {
    api.get("/suppliers").then(setSuppliers).catch(() => setSuppliers([]));
  }, []);
  const errMsg = (e, f = "Failed") => {
    const d = e?.response?.data?.detail;
    if (!d) return f;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
    return JSON.stringify(d);
  };
  const addQuote = async () => {
    if (!form.supplier_name.trim()) { toast.error("Supplier is required"); return; }
    if (!form.total || form.total <= 0) { toast.error("Total must be positive"); return; }
    setBusy(true);
    try {
      await api.post(`/material-requests/${req.id}/quotes`, {
        supplier_name: form.supplier_name.trim(),
        total: parseFloat(form.total),
        delivery_date: form.delivery_date || null,
        notes: form.notes,
      });
      setForm({ supplier_name: "", total: 0, delivery_date: "", notes: "" });
      toast.success("Quote added");
      onChange();
    } catch (e) { toast.error(errMsg(e, "Failed to add quote")); }
    finally { setBusy(false); }
  };
  const removeQuote = async (qid) => {
    if (!window.confirm("Remove this quote?")) return;
    try {
      await api.del(`/material-requests/${req.id}/quotes/${qid}`);
      onChange();
    } catch (e) { toast.error(errMsg(e, "Failed to remove quote")); }
  };
  const submitForApproval = async () => {
    setBusy(true);
    try {
      await api.post(`/material-requests/${req.id}/submit-approval`, {});
      toast.success("Submitted for approval — PM/Analyst notified");
      onChange();
    } catch (e) { toast.error(errMsg(e, "Failed to submit")); }
    finally { setBusy(false); }
  };
  const approveQuote = async (qid) => {
    setBusy(true);
    try {
      await api.post(`/material-requests/${req.id}/approve-quote`, { quote_id: qid, notes: approvalNotes });
      toast.success("Quote approved — purchasing cleared to proceed");
      onChange();
    } catch (e) { toast.error(errMsg(e, "Failed to approve")); }
    finally { setBusy(false); }
  };

  const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
  const cheapest = quotes.length ? quotes.reduce((a, b) => (a.total < b.total ? a : b)) : null;

  return (
    <div className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white rounded-lg p-3 space-y-3" data-testid="mr-quotes-section">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-widest text-purple-800 inline-flex items-center gap-1.5">
          <DollarSign className="w-3.5 h-3.5" /> Quotes / RFQ ({quotes.length}/3)
        </div>
        {canSubmitForApproval && (
          <button
            data-testid="mr-quotes-submit-approval"
            onClick={submitForApproval} disabled={busy}
            className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-3 py-1.5 rounded disabled:opacity-50"
          >
            <Send className="w-3.5 h-3.5" /> Submit for approval
          </button>
        )}
      </div>

      {/* Quote rows */}
      {quotes.length === 0 && (
        <div className="text-[11px] text-slate-500 italic">No quotes yet — Purchasing collects up to 3 quotes before requesting approval.</div>
      )}
      {quotes.length > 0 && (
        <div className="space-y-1.5">
          {quotes.map((q) => {
            const isCheapest = cheapest && q.id === cheapest.id && quotes.length > 1;
            const isSelected = req.selected_quote_id === q.id;
            return (
              <div key={q.id} className={`border rounded-lg p-2 ${isSelected ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white"}`} data-testid={`mr-quote-${q.id}`}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-slate-900">{q.supplier_name}</span>
                      {isCheapest && <span className="text-[9px] font-bold uppercase bg-blue-100 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded">Cheapest</span>}
                      {isSelected && <span className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded">✓ Approved</span>}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      Added by {q.added_by_name} on {(q.added_at || "").slice(0, 10)}
                      {q.delivery_date && <> · Delivery: <b>{q.delivery_date}</b></>}
                    </div>
                    {q.notes && <div className="text-[11px] text-slate-600 italic mt-0.5">{q.notes}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-base font-bold tabular-nums text-slate-900">{money(q.total)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    {canApproveQuote && !isSelected && (
                      <button
                        data-testid={`mr-quote-approve-${q.id}`}
                        onClick={() => approveQuote(q.id)}
                        disabled={busy}
                        className="text-[10px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded inline-flex items-center gap-1 disabled:opacity-50"
                      ><Check className="w-3 h-3" /> Approve</button>
                    )}
                    {canAddQuotes && !isSelected && (
                      <button
                        data-testid={`mr-quote-remove-${q.id}`}
                        onClick={() => removeQuote(q.id)}
                        title="Remove quote"
                        className="p-1 rounded text-red-600 hover:bg-red-50"><Trash2 className="w-3 h-3" /></button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Approver notes */}
      {canApproveQuote && (
        <input
          value={approvalNotes}
          onChange={(e) => setApprovalNotes(e.target.value)}
          placeholder="Approval notes (optional — e.g. 'best delivery date')"
          className="w-full text-xs border border-slate-200 rounded px-2 py-1"
          data-testid="mr-quote-approval-notes"
        />
      )}

      {/* Add quote form */}
      {canAddQuotes && quotes.length < 3 && (
        <div className="border-t border-purple-100 pt-2">
          <div className="text-[10px] font-bold uppercase text-purple-700 mb-1">Add a quote</div>
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-4">
              <SupplierAutocomplete
                value={form.supplier_name}
                supplierId={form.supplier_id}
                onChange={({ name, id }) => setForm({ ...form, supplier_name: name, supplier_id: id || "" })}
                testId="mr-quote-supplier"
                placeholder="Supplier name"
              />
            </div>
            <div className="col-span-2">
              <input type="number" step="0.01"
                data-testid="mr-quote-total"
                value={form.total || ""}
                onChange={(e) => setForm({ ...form, total: parseFloat(e.target.value) || 0 })}
                placeholder="Total"
                className="w-full text-xs text-right tabular-nums rounded border border-slate-300 px-2 py-1.5" />
            </div>
            <div className="col-span-3">
              <input type="date"
                value={form.delivery_date}
                onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
                className="w-full text-xs rounded border border-slate-300 px-2 py-1.5" />
            </div>
            <div className="col-span-2">
              <input value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Notes"
                className="w-full text-xs rounded border border-slate-300 px-2 py-1.5" />
            </div>
            <div className="col-span-1">
              <button
                data-testid="mr-quote-add-btn"
                onClick={addQuote} disabled={busy}
                className="w-full text-xs bg-purple-600 hover:bg-purple-700 text-white font-bold rounded px-2 py-1.5 inline-flex items-center justify-center gap-1 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
